import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Plus, Trash2, Download, Receipt, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const CAT_TONE = {
  utilities: "border-sky-500/30 text-sky-300",
  landscaping: "border-emerald-500/30 text-emerald-300",
  insurance: "border-amber-500/30 text-amber-300",
  management: "border-indigo-500/30 text-indigo-300",
  repairs: "border-rose-500/30 text-rose-300",
  cleaning: "border-teal-500/30 text-teal-300",
  security: "border-violet-500/30 text-violet-300",
  legal: "border-zinc-500/30 text-zinc-300",
  reserves: "border-orange-500/30 text-orange-300",
  other: "border-white/10 text-zinc-400",
};

/**
 * ExpensesCard — real expense ledger. Powers the Reserve Fund Forecast so
 * projections stop treating refunds as an expense proxy.
 */
export function ExpensesCard({ compact = false, defaultPropertyId = "all" }) {
  const { properties } = useProperty();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    property_id: defaultPropertyId !== "all" ? defaultPropertyId : (properties[0]?.id || ""),
    date: new Date().toISOString().slice(0, 10),
    category: "utilities",
    amount: "",
    vendor: "",
    memo: "",
  });
  const [categories, setCategories] = useState([]);
  const [yoy, setYoy] = useState(null);
  const fileRef = useRef(null);

  const scope = defaultPropertyId;

  const load = useCallback(async () => {
    try {
      const params = { year, limit: compact ? 25 : 200 };
      if (scope && scope !== "all") params.property_id = scope;
      const [{ data: l }, { data: s }, { data: c }, { data: y }] = await Promise.all([
        api.get("/admin/expenses", { params }),
        api.get("/admin/expenses/summary", { params: { year, property_id: scope !== "all" ? scope : undefined } }),
        api.get("/admin/expenses/categories"),
        api.get("/admin/expenses/yoy-trend", { params: { years: 3, property_id: scope !== "all" ? scope : undefined } }),
      ]);
      setItems(l.items || []);
      setSummary(s);
      setCategories(c.items || []);
      setYoy(y);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load expenses");
    }
  }, [year, scope, compact]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/expenses", {
        ...form,
        amount: Number(form.amount),
      });
      toast.success("Expense saved");
      setDialogOpen(false);
      setForm((f) => ({ ...f, amount: "", vendor: "", memo: "" }));
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/admin/expenses/${id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Delete failed");
    }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/expenses/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errs = (data.errors || []).length;
      toast.success(`${data.created} expense${data.created === 1 ? "" : "s"} imported${errs ? ` · ${errs} row${errs === 1 ? "" : "s"} skipped` : ""}`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const totalByCat = useMemo(() => summary?.by_category || [], [summary]);

  return (
    <Card className={"border-white/10 bg-card " + (compact ? "p-3" : "p-5")} data-testid="expenses-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="overline">Ledger</div>
          <div className="text-base font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <Receipt className="h-4 w-4 text-orange-400" /> Expense Ledger
          </div>
          {!compact && (
            <div className="text-xs text-zinc-500 mt-1">
              Real operating expenses. Powers the Reserve Fund Forecast — no more refund proxy.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 bg-white/[0.04] border-white/10 h-8" data-testid="expenses-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
              {[year + 1, year, year - 1, year - 2].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <div className="rounded-md border border-white/5 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Annual total</div>
            <div className="tabular-nums text-white text-sm" data-testid="expenses-total">{money(summary.total)}</div>
          </div>
          {totalByCat.slice(0, 3).map((c) => (
            <div key={c.category} className="rounded-md border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{c.category}</div>
              <div className="tabular-nums text-zinc-300 text-sm">{money(c.amount)}</div>
            </div>
          ))}
        </div>
      )}

      {yoy && yoy.series && yoy.series.length > 0 && (
        <div className="rounded-md border border-white/5 p-3 mb-3" data-testid="expenses-yoy-card">
          <div className="flex items-center justify-between mb-2">
            <div className="inline-flex items-center gap-2">
              <TrendingUp className="h-3.5 w-3.5 text-orange-400" />
              <span className="text-xs uppercase tracking-widest text-zinc-500">3-year monthly trend</span>
            </div>
            {yoy.yoy_delta && (
              <Badge variant="outline"
                     className={yoy.yoy_delta.pct_change >= 0
                                ? "border-rose-500/40 text-rose-300 bg-rose-500/[0.08]"
                                : "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.08]"}
                     data-testid="expenses-yoy-badge">
                {yoy.yoy_delta.pct_change >= 0 ? "▲" : "▼"} {(yoy.yoy_delta.pct_change * 100).toFixed(1)}% vs {yoy.yoy_delta.prior_year}
              </Badge>
            )}
          </div>
          <div style={{ width: "100%", height: 200 }} data-testid="expenses-yoy-chart">
            <ResponsiveContainer minHeight={200}>
              <LineChart data={yoy.series} margin={{ top: 6, right: 12, bottom: 4, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: "#a1a1aa", fontSize: 10 }} />
                <YAxis tick={{ fill: "#a1a1aa", fontSize: 10 }}
                       tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                         formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
                {yoy.years.map((y, i) => (
                  <Line key={y} type="monotone" dataKey={String(y)}
                        stroke={i === yoy.years.length - 1 ? "#f97316" : i === yoy.years.length - 2 ? "#818cf8" : "#71717a"}
                        strokeWidth={i === yoy.years.length - 1 ? 2 : 1.5}
                        strokeDasharray={i < yoy.years.length - 1 ? "4 3" : "0"}
                        dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {(yoy.yearly_totals || []).map((yt) => (
              <div key={yt.year} className="rounded-md bg-white/[0.02] border border-white/5 px-2 py-1.5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{yt.year}</div>
                <div className="text-xs tabular-nums text-white">{money(yt.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        <Button onClick={() => setDialogOpen(true)} size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="expense-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add expense
        </Button>
        <input ref={fileRef} type="file" accept=".csv" hidden onChange={(e) => uploadCsv(e.target.files?.[0])} data-testid="expense-file-input" />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="border-white/10 rounded-full" disabled={busy} data-testid="expense-upload-btn">
          <Upload className="h-3.5 w-3.5 mr-1" /> Upload CSV
        </Button>
        <a href={`${API}/admin/expenses/template.csv`}
           className="text-xs text-orange-300 hover:text-orange-200 inline-flex items-center gap-1 self-center"
           data-testid="expense-template-download">
          <Download className="h-3 w-3" /> Template
        </a>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500" data-testid="expenses-empty">
          No expenses recorded for {year} yet. Add one manually or upload a CSV.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-white/5">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.03]">
              <tr>
                <th className="text-left px-3 py-2 text-zinc-400">Date</th>
                <th className="text-left px-3 py-2 text-zinc-400">Category</th>
                <th className="text-left px-3 py-2 text-zinc-400">Vendor</th>
                <th className="text-right px-3 py-2 text-zinc-400">Amount</th>
                <th className="text-right px-3 py-2 text-zinc-400 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-white/5" data-testid={`expense-row-${e.id}`}>
                  <td className="px-3 py-1.5 text-zinc-400 tabular-nums">{e.date}</td>
                  <td className="px-3 py-1.5">
                    <Badge variant="outline" className={CAT_TONE[e.category] || CAT_TONE.other}>
                      {e.category}
                    </Badge>
                  </td>
                  <td className="px-3 py-1.5 text-zinc-300 truncate max-w-[200px]">{e.vendor || "—"}</td>
                  <td className="px-3 py-1.5 text-right text-white tabular-nums">{money(e.amount)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => remove(e.id)} className="text-zinc-500 hover:text-red-400" data-testid={`expense-delete-${e.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[440px]" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-orange-400" /> Add expense</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              One-time entry — pick a community, date, category and amount. Vendor and memo are optional.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Community</Label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="expense-property">
                  <SelectValue placeholder="Pick a community" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                       className="bg-white/[0.04] border-white/10" data-testid="expense-date" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       placeholder="0.00" className="bg-white/[0.04] border-white/10" data-testid="expense-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="expense-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Vendor (optional)</Label>
              <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                     placeholder="e.g. SCE" className="bg-white/[0.04] border-white/10" data-testid="expense-vendor" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Memo (optional)</Label>
              <Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}
                     placeholder="January invoice" className="bg-white/[0.04] border-white/10" data-testid="expense-memo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={create} disabled={busy || !form.property_id || !form.amount}
                    className="bg-orange-500 hover:bg-orange-600" data-testid="expense-save-btn">
              {busy ? "Saving…" : "Save expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default ExpensesCard;
